// Pure-function emoji grid generator. No I/O, no DOM, no Nostr.
//
// Glyph mapping is intentionally minimal and matches
// Vault/Architecture/Share Format.md. The user's OS font stack (set
// elsewhere via the `.emoji-grid` CSS class) decides actual rendering.

import type { GroupBar, GroupTier, TileResult } from './types.js';

const TILE_GLYPHS: Record<TileResult, string> = {
  correct: '\u{1F7E9}', // green square
  present: '\u{1F7E7}', // orange square (canonical brand "present")
  absent: '\u{2B1C}', // white large square (dark mode clients re-render as black)
  // `neutral` and `revealed` should never appear in a published share;
  // collapse them to `absent` so we still produce a valid grid if a
  // caller forgets to filter.
  neutral: '\u{2B1C}',
  revealed: '\u{1F7E9}',
};

const TIER_GLYPHS: Record<GroupTier, string> = {
  easy: '\u{1F7E8}', // yellow square (Connections "easy" tier)
  medium: '\u{1F7E9}', // green
  hard: '\u{1F7E6}', // blue
  tricky: '\u{1F7EA}', // purple
};

/**
 * Render a cell-by-cell emoji grid. Each row is joined into a single
 * string of glyphs; rows are joined with `\n`.
 */
export function emojiGrid(rows: TileResult[][]): string {
  return rows
    .map((row) => row.map((cell) => TILE_GLYPHS[cell]).join(''))
    .join('\n');
}

/**
 * Render a Connections-style stack of group bars. Each bar is a tier's
 * glyph repeated `count` times; bars are joined with `\n`.
 */
export function groupBars(bars: GroupBar[]): string {
  return bars
    .map((bar) => TIER_GLYPHS[bar.tier].repeat(Math.max(0, bar.count)))
    .join('\n');
}
